import './sponsorship-card.scss';

import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Badge, BadgeProps } from '@sola/ark-core/atoms/badge/badge.js';
import { LinkCard } from '@sola/ark-core/molecules/link-card/link-card.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';
import { getAgeFromBirthday } from '@sola/shared-utils/children/utils.js';

import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar';

const getBadgeProps = (
  commitmentStatus: string,
  t: TFunction,
): BadgeProps | undefined => {
  switch (commitmentStatus) {
    case 'Closed':
      return {
        iconBefore: 'alert-triangle',
        label: t('my:sponsorshipRelationship.prior'),
        color: 'warning',
      };
    // TODO: Departing, Pending
  }
};

const useSponsorshipCardData = (commitment: GraphQLCommitment) => {
  const { t } = useTranslation(['children', 'my']);
  const { participant } = commitment;
  if (!participant) {
    return {};
  }

  const { name, birthdate, country } = participant;
  const birthdayText = birthdate
    ? t('children:sac.age', {
        count: Number(getAgeFromBirthday(birthdate)),
        birthday: birthdate,
      })
    : undefined;

  const badgeProps = getBadgeProps(commitment.status, t);

  return {
    name,
    country,
    birthdayText,
    badgeProps,
  };
};

export type SponsorshipCardProps = {
  commitment: GraphQLCommitment;
  href: string;
  onClick?: () => void;
};

export const SponsorshipCard: React.FC<SponsorshipCardProps> = ({
  commitment,
  href,
  onClick,
}) => {
  const { name, country, birthdayText, badgeProps } =
    useSponsorshipCardData(commitment);

  return (
    <LinkCard
      className="SponsorshipCard"
      href={href}
      onClick={onClick}
      graphic={<CommitmentAvatar commitment={commitment} size="xl" />}
    >
      {badgeProps && (
        <div className="SponsorshipCard__badge">
          <Badge responsive {...badgeProps} />
        </div>
      )}
      <div className="SponsorshipCard__title heading-xs md:heading-sm">
        {name}
      </div>

      {birthdayText && (
        <div className="SponsorshipCard__meta flex items-center gap-comfy-xs md:gap-comfy-sm">
          <div className="flex shrink-0 w-6 h-6">
            <ArkIcon iconName="birthday-cake" size="1.5rem" />
          </div>
          <div className="body-sm md:body-md">{birthdayText}</div>
        </div>
      )}

      <div className="SponsorshipCard__meta flex items-center gap-comfy-xs md:gap-comfy-sm">
        <div className="flex shrink-0 w-6 h-6">
          <ArkIcon iconName="globe-05" size="1.5rem" />
        </div>
        <div className="body-sm md:body-md">{country}</div>
      </div>
    </LinkCard>
  );
};
